// Client-side country guess for pre-selecting the phone dialing code.
// Priority when combined in the UI: edge IP (if the CDN forwards it) → timezone → language → default.
// Timezone detection works offline and on every platform (web / PWA / Capacitor),
// and is far more accurate than navigator.language for guessing the country.

// IANA timezone → ISO2 country. Curated for Yalola's markets (francophone Africa +
// Europe + Americas) with broad worldwide coverage. Unknown zones fall back to locale.
const TZ_TO_COUNTRY = {
  // ── Africa ──
  "Africa/Kinshasa": "CD", "Africa/Lubumbashi": "CD",
  "Africa/Brazzaville": "CG", "Africa/Abidjan": "CI", "Africa/Accra": "GH",
  "Africa/Dakar": "SN", "Africa/Bamako": "ML", "Africa/Ouagadougou": "BF",
  "Africa/Niamey": "NE", "Africa/Conakry": "GN", "Africa/Cotonou": "BJ",
  "Africa/Lome": "TG", "Africa/Douala": "CM", "Africa/Bangui": "CF",
  "Africa/Ndjamena": "TD", "Africa/Libreville": "GA", "Africa/Malabo": "GQ",
  "Africa/Lagos": "NG", "Africa/Porto-Novo": "BJ", "Africa/Luanda": "AO",
  "Africa/Kigali": "RW", "Africa/Bujumbura": "BI", "Africa/Kampala": "UG",
  "Africa/Nairobi": "KE", "Africa/Dar_es_Salaam": "TZ", "Africa/Addis_Ababa": "ET",
  "Africa/Khartoum": "SD", "Africa/Juba": "SS", "Africa/Mogadishu": "SO",
  "Africa/Djibouti": "DJ", "Africa/Asmara": "ER", "Africa/Maputo": "MZ",
  "Africa/Harare": "ZW", "Africa/Lusaka": "ZM", "Africa/Blantyre": "MW",
  "Africa/Johannesburg": "ZA", "Africa/Maseru": "LS", "Africa/Mbabane": "SZ",
  "Africa/Gaborone": "BW", "Africa/Windhoek": "NA", "Indian/Antananarivo": "MG",
  "Indian/Mauritius": "MU", "Indian/Mayotte": "YT", "Indian/Comoro": "KM",
  "Africa/Casablanca": "MA", "Africa/El_Aaiun": "EH", "Africa/Algiers": "DZ",
  "Africa/Tunis": "TN", "Africa/Tripoli": "LY", "Africa/Cairo": "EG",
  "Africa/Nouakchott": "MR", "Africa/Bissau": "GW", "Africa/Banjul": "GM",
  "Africa/Freetown": "SL", "Africa/Monrovia": "LR", "Atlantic/Cape_Verde": "CV",

  // ── Europe ──
  "Europe/Paris": "FR", "Europe/Brussels": "BE", "Europe/London": "GB",
  "Europe/Dublin": "IE", "Europe/Amsterdam": "NL", "Europe/Luxembourg": "LU",
  "Europe/Berlin": "DE", "Europe/Zurich": "CH", "Europe/Vienna": "AT",
  "Europe/Madrid": "ES", "Europe/Lisbon": "PT", "Europe/Rome": "IT",
  "Europe/Monaco": "MC", "Europe/Andorra": "AD", "Europe/Copenhagen": "DK",
  "Europe/Oslo": "NO", "Europe/Stockholm": "SE", "Europe/Helsinki": "FI",
  "Europe/Warsaw": "PL", "Europe/Prague": "CZ", "Europe/Bratislava": "SK",
  "Europe/Budapest": "HU", "Europe/Bucharest": "RO", "Europe/Sofia": "BG",
  "Europe/Athens": "GR", "Europe/Istanbul": "TR", "Europe/Kyiv": "UA",
  "Europe/Kiev": "UA", "Europe/Moscow": "RU", "Europe/Zagreb": "HR",
  "Europe/Belgrade": "RS", "Europe/Ljubljana": "SI", "Europe/Vilnius": "LT",
  "Europe/Riga": "LV", "Europe/Tallinn": "EE", "Atlantic/Reykjavik": "IS",

  // ── Americas ──
  "America/New_York": "US", "America/Detroit": "US", "America/Chicago": "US",
  "America/Denver": "US", "America/Phoenix": "US", "America/Los_Angeles": "US",
  "America/Anchorage": "US", "Pacific/Honolulu": "US",
  "America/Toronto": "CA", "America/Montreal": "CA", "America/Vancouver": "CA",
  "America/Edmonton": "CA", "America/Winnipeg": "CA", "America/Halifax": "CA",
  "America/Mexico_City": "MX", "America/Tijuana": "MX", "America/Monterrey": "MX",
  "America/Sao_Paulo": "BR", "America/Bahia": "BR", "America/Fortaleza": "BR",
  "America/Manaus": "BR", "America/Buenos_Aires": "AR",
  "America/Argentina/Buenos_Aires": "AR", "America/Santiago": "CL",
  "America/Bogota": "CO", "America/Lima": "PE", "America/Caracas": "VE",
  "America/La_Paz": "BO", "America/Asuncion": "PY", "America/Montevideo": "UY",
  "America/Guayaquil": "EC", "America/Panama": "PA", "America/Havana": "CU",
  "America/Santo_Domingo": "DO", "America/Port-au-Prince": "HT",
  "America/Guatemala": "GT", "America/Costa_Rica": "CR", "America/El_Salvador": "SV",
  "America/Tegucigalpa": "HN", "America/Managua": "NI",

  // ── Middle East & Asia ──
  "Asia/Jerusalem": "IL", "Asia/Beirut": "LB", "Asia/Amman": "JO",
  "Asia/Damascus": "SY", "Asia/Baghdad": "IQ", "Asia/Riyadh": "SA",
  "Asia/Dubai": "AE", "Asia/Qatar": "QA", "Asia/Kuwait": "KW",
  "Asia/Bahrain": "BH", "Asia/Muscat": "OM", "Asia/Tehran": "IR",
  "Asia/Karachi": "PK", "Asia/Kolkata": "IN", "Asia/Calcutta": "IN",
  "Asia/Dhaka": "BD", "Asia/Colombo": "LK", "Asia/Kathmandu": "NP",
  "Asia/Bangkok": "TH", "Asia/Ho_Chi_Minh": "VN", "Asia/Jakarta": "ID",
  "Asia/Manila": "PH", "Asia/Kuala_Lumpur": "MY", "Asia/Singapore": "SG",
  "Asia/Hong_Kong": "HK", "Asia/Shanghai": "CN", "Asia/Taipei": "TW",
  "Asia/Seoul": "KR", "Asia/Tokyo": "JP",

  // ── Oceania ──
  "Australia/Sydney": "AU", "Australia/Melbourne": "AU", "Australia/Perth": "AU",
  "Australia/Brisbane": "AU", "Pacific/Auckland": "NZ", "Pacific/Fiji": "FJ",
};

export const timezoneToCountry = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TZ_TO_COUNTRY[tz] || null;
  } catch {
    return null;
  }
};

export const localeToCountry = () => {
  try {
    const loc = navigator.language || (navigator.languages && navigator.languages[0]) || "";
    const m = loc.match(/[-_]([A-Za-z]{2})\b/);
    return m ? m[1].toUpperCase() : null;
  } catch {
    return null;
  }
};

// Best offline client-side guess: timezone (accurate) → locale region → null.
export const detectClientCountry = () => timezoneToCountry() || localeToCountry() || null;
